import type { LocatorJson } from "@msb/reader-bridge";
import { useMemo } from "preact/hooks";
import { HIGHLIGHT_COLORS, type Annotation } from "../annotations.ts";
import { defineMessages, intlLocale, useLocale, useT, type Translate } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";
import type { Bookmark } from "../storage.ts";

interface Props {
  annotations: Annotation[];
  bookmarks: Bookmark[];
  positionCount: number;
  onSelectAnnotation(annotation: Annotation): void;
  onEditAnnotation(annotation: Annotation): void;
  onDeleteAnnotation(annotation: Annotation): void;
  onSelectBookmark(bookmark: Bookmark): void;
  onDeleteBookmark(bookmark: Bookmark): void;
}

const messages = defineMessages(
  {
    pageOf: "{position} sur {count}",
    percent: "{value} %",
    empty: "Aucune note ni signet pour l’instant.",
    emptyHint: "Sélectionnez un passage pour le surligner ou y ajouter une note, ou marquez la page avec le bouton signet.",
    list: "Notes et signets du livre",
    bookmark: "Signet",
    deleteBookmark: "Supprimer ce signet",
    edit: "Modifier cette annotation",
    delete: "Supprimer cette annotation",
  },
  {
    pageOf: "{position} of {count}",
    percent: "{value}%",
    empty: "No notes or bookmarks yet.",
    emptyHint: "Select a passage to highlight it or add a note, or mark the page with the bookmark button.",
    list: "Notes and bookmarks of the book",
    bookmark: "Bookmark",
    deleteBookmark: "Delete this bookmark",
    edit: "Edit this annotation",
    delete: "Delete this annotation",
  },
);

function where(locator: LocatorJson, positionCount: number, t: Translate<typeof messages.fr>): string {
  const position = locator.locations?.position;
  if (typeof position === "number" && positionCount > 0) return t("pageOf", { position, count: positionCount });
  const total = locator.locations?.totalProgression;
  return typeof total === "number" ? t("percent", { value: Math.round(total * 100) }) : "";
}

const placeOf = (locator: LocatorJson) => locator.locations?.totalProgression ?? locator.locations?.progression ?? 0;

type Entry = { kind: "bookmark"; bookmark: Bookmark } | { kind: "annotation"; annotation: Annotation };
const entryLocator = (entry: Entry) => (entry.kind === "bookmark" ? entry.bookmark.locator : entry.annotation.locator);
const entryDate = (entry: Entry) => (entry.kind === "bookmark" ? entry.bookmark.createdAt : entry.annotation.createdAt);

/** « Notes et signets » : signets, surlignages et notes du livre, dans une seule liste, dans l'ordre de lecture. */
export function NotesPanel(props: Props) {
  const { annotations, bookmarks, positionCount } = props;
  const t = useT(messages);
  const locale = useLocale();
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: "medium" }), [locale]);
  const entries: Entry[] = [
    ...bookmarks.map((bookmark) => ({ kind: "bookmark" as const, bookmark })),
    ...annotations.map((annotation) => ({ kind: "annotation" as const, annotation })),
  ].sort((x, y) => placeOf(entryLocator(x)) - placeOf(entryLocator(y)) || entryDate(x).localeCompare(entryDate(y)));
  if (entries.length === 0) {
    return (
      <div class="msb-empty">
        <Icon name="bookmarks" size={40} />
        <p>{t("empty")}</p>
        <p>{t("emptyHint")}</p>
      </div>
    );
  }
  return (
    <ol class="msb-list" aria-label={t("list")}>
      {entries.map((entry) => {
        if (entry.kind === "bookmark") {
          const b = entry.bookmark;
          return (
            <li key={`b-${b.id}`} class="msb-bookmark">
              <button type="button" class="msb-list__item" onClick={() => props.onSelectBookmark(b)}>
                <span class="msb-bookmark__title">{b.locator.title || t("bookmark")}</span>
                <span class="msb-bookmark__meta">
                  {where(b.locator, positionCount, t)} · {dateFormat.format(new Date(b.createdAt))}
                </span>
              </button>
              {/* Un signet, à la place du bouton de note d'une annotation. */}
              <span class="msb-icon-button msb-notes__kind" role="img" aria-label={t("bookmark")} title={t("bookmark")}>
                <Icon name="bookmarkOutline" />
              </span>
              <button type="button" class="msb-icon-button" aria-label={t("deleteBookmark")} onClick={() => props.onDeleteBookmark(b)}>
                <Icon name="delete" />
              </button>
            </li>
          );
        }
        const annotation = entry.annotation;
        const color = HIGHLIGHT_COLORS.find((c) => c.id === annotation.color) ?? HIGHLIGHT_COLORS[0]!;
        return (
          <li key={`a-${annotation.id}`} class="msb-bookmark">
            <button type="button" class="msb-list__item msb-annotation-item" onClick={() => props.onSelectAnnotation(annotation)}>
              <span class="msb-annotation-item__quote" style={{ boxShadow: `inset 4px 0 0 ${color.tint}` }} data-color={color.id}>
                {annotation.locator.text?.highlight}
              </span>
              {annotation.note && <span class="msb-annotation-item__note">{annotation.note}</span>}
              <span class="msb-bookmark__meta">
                {annotation.locator.title ? `${annotation.locator.title} · ` : ""}
                {where(annotation.locator, positionCount, t)} · {dateFormat.format(new Date(annotation.createdAt))}
              </span>
            </button>
            <button type="button" class="msb-icon-button" aria-label={t("edit")} onClick={() => props.onEditAnnotation(annotation)}>
              <Icon name="note" />
            </button>
            <button type="button" class="msb-icon-button" aria-label={t("delete")} onClick={() => props.onDeleteAnnotation(annotation)}>
              <Icon name="delete" />
            </button>
          </li>
        );
      })}
    </ol>
  );
}
